/**
 * What a member, an invite, a group or a study link gives in a study (CONTEXT.md, Grant; users
 * decisions 34 and 41). The shape matches spdata-api's `src/users/grant.ts`, so a grant goes from the
 * wire to `<GrantEditor>` and back without a translation in between.
 */

/** Every form of the study, or the base64 ids of the forms named. */
export type FormAccess = 'all' | string[];

export interface Grant {
  viewForms: FormAccess;
  addEntryForms: FormAccess;
  viewEntries: 'all' | 'own';
  export: boolean;
  editCases: boolean;
  manageStudy: boolean;
  manageStudyFlags: boolean;
  manageUsers: boolean;
  manageForms: boolean;
  manageApiKeys: boolean;
  /**
   * View child studies: whether the holder may open the study's Child studies section
   * (`VIEW_CHILD_STUDIES`, ENG-10328). Manage study holds it. Not the permissions read's
   * `viewChildStudies`, which also asks whether the study has a child study to show.
   */
  childStudies: boolean;
}

/** A grant of nothing: no forms, own entries, no other permission. */
export const NO_GRANT: Readonly<Grant> = Object.freeze({
  viewForms: [],
  addEntryForms: [],
  viewEntries: 'own',
  export: false,
  editCases: false,
  manageStudy: false,
  manageStudyFlags: false,
  manageUsers: false,
  manageForms: false,
  manageApiKeys: false,
  childStudies: false
});

/**
 * One label per permission, as locale keys (users decisions 43 and 120). `<GrantEditor>`, the People
 * row's sentence and its expanded panel all read this table, so a permission is called the same
 * everywhere. `viewEntries` is labelled for the permission it gives, all entries.
 */
export const GRANT_LABEL_KEYS: Readonly<Record<keyof Grant, string>> = {
  viewForms: 'users_permission_view_forms',
  addEntryForms: 'users_permission_add_entries',
  viewEntries: 'users_permission_view_all_entries',
  export: 'users_permission_export',
  editCases: 'users_permission_edit_cases',
  manageStudy: 'users_permission_manage_study',
  manageStudyFlags: 'users_permission_manage_study_flags',
  manageUsers: 'users_permission_manage_users',
  manageForms: 'users_permission_manage_forms',
  manageApiKeys: 'users_permission_manage_api_keys',
  childStudies: 'users_permission_child_studies'
};

/** What each value of `viewEntries` is called where a grant is read rather than edited. */
export const VIEW_ENTRIES_LABEL_KEYS: Readonly<Record<Grant['viewEntries'], string>> = {
  all: GRANT_LABEL_KEYS.viewEntries,
  own: 'users_permission_view_own_entries'
};

/** The fields in the order every surface lists them. */
export const GRANT_FIELDS = Object.keys(GRANT_LABEL_KEYS) as (keyof Grant)[];

/** The two form lists. */
export type FormField = 'viewForms' | 'addEntryForms';
/** The eight permissions a grant gives or doesn't. */
export type FlagField = Exclude<keyof Grant, FormField | 'viewEntries'>;

export const FORM_FIELDS: readonly FormField[] = ['viewForms', 'addEntryForms'];
export const FLAG_FIELDS = GRANT_FIELDS.filter(
  (field): field is FlagField => field !== 'viewEntries' && !(FORM_FIELDS as readonly string[]).includes(field)
);
/** The five manage permissions, which Manage study holds the other four of (users decision 22). */
export const MANAGE_FIELDS = FLAG_FIELDS.filter(field => field.startsWith('manage'));

/**
 * How the member row's sentence words each permission (users decision 30), kept beside the labels so
 * a permission's names change in one place: a form list as all forms or a count, entries as all or
 * own, and each other permission as what its holder does.
 */
export const GRANT_PHRASE_KEYS = {
  viewForms: { all: 'users_access_forms_all', some: 'users_access_forms_count' },
  addEntryForms: { all: 'users_access_add_entries_all', some: 'users_access_add_entries_count' },
  viewEntries: { all: 'users_access_entries_all', own: 'users_access_entries_own' },
  export: 'users_access_export',
  editCases: 'users_access_edit_cases',
  manageStudy: 'users_access_manages_study',
  manageStudyFlags: 'users_access_manages_study_flags',
  manageUsers: 'users_access_manages_users',
  manageForms: 'users_access_manages_forms',
  manageApiKeys: 'users_access_manages_api_keys',
  childStudies: 'users_access_child_studies'
} as const satisfies Record<keyof Grant, string | Record<string, string>>;

const sameFormAccess = (a: FormAccess, b: FormAccess): boolean => {
  // 'all' covers shared forms and forms added later, so no list equals it, however many it names.
  if (a === 'all' || b === 'all') {
    return a === b;
  }
  const left = new Set(a);
  const right = new Set(b);
  return left.size === right.size && [...left].every(formId => right.has(formId));
};

/**
 * Whether two grants give the same (users decision 36). Form lists compare as sets, and neither grant
 * is changed. Every surface that edits a grant is dirty when `!sameGrant(saved, draft)`.
 */
export const sameGrant = (a: Grant, b: Grant): boolean =>
  FORM_FIELDS.every(field => sameFormAccess(a[field], b[field])) &&
  a.viewEntries === b.viewEntries &&
  FLAG_FIELDS.every(field => a[field] === b[field]);

const copyFormAccess = (access: FormAccess): FormAccess => (access === 'all' ? 'all' : [...access]);

/**
 * A grant as a request body sends it: the eleven fields and nothing else, with the form lists copied, so
 * a draft carrying stray fields or edited after the body was built sends neither. Every grant write
 * goes through this one serializer (users decision 33).
 */
export const serializeGrant = (grant: Grant): Grant => ({
  viewForms: copyFormAccess(grant.viewForms),
  addEntryForms: copyFormAccess(grant.addEntryForms),
  viewEntries: grant.viewEntries,
  ...Object.fromEntries(FLAG_FIELDS.map(field => [field, grant[field]])) as Pick<Grant, FlagField>
});

/** Every form, or at least one named. */
const reachesAnyForm = (access: FormAccess): boolean => access === 'all' || access.length > 0;

/**
 * Whether a grant lets its holder add entries: entry access to every form or to at least one (users
 * decisions 26 and 38). No grant carries a flag of its own for it.
 */
export const canAddEntries = (grant: Pick<Grant, 'addEntryForms'>): boolean =>
  reachesAnyForm(grant.addEntryForms);

/**
 * Whether `GET study/:id/forms` answers a grant's holder rather than refusing them with a 400: Manage
 * forms, which lists every form's definition (users decision 23), or a form they may view. A read for
 * one case (`forCase`, the route's `?caseId=`) works each form out from that case's entries, so there
 * Manage forms counts for nothing. A forms read is not asked of a holder this says no to, because the
 * refusal follows from their own access and toasting it reports an error they cannot act on (ENG-10306).
 *
 * A `staff` account is answered whatever its grant, on either read, as spdata-api's `readVisibleForms`
 * lets `user.staff` through (ENG-10320).
 */
export const canReadFormList = (
  grant: Pick<Grant, 'manageForms' | 'viewForms'>,
  { forCase = false, staff = false }: { forCase?: boolean; staff?: boolean } = {}
): boolean => staff || (grant.manageForms && !forCase) || reachesAnyForm(grant.viewForms);
